import { z } from "zod";
import { shareLinkFor } from "../share";
/**
 * A plan shared by link (`D273`): the wire.
 *
 * ── WHAT A "LINK" IS BEFORE LAUNCH ─────────────────────────────────────────
 * A share is an opaque twelve-character id the server mints, which the app
 * shows as a CODE (`ABCD EFGH 2345`) and sends in the share sheet's text.
 * Whoever has the app types or pastes it into "Importar um plano". While
 * `D257`'s switch is off that is all a share carries; once
 * `SHARE_LINK_BASE_URL` names a base, the same text also carries
 * `<base>?plan=<id>&ref=<ref>` — the site opens, keeps the id on the device,
 * and the app opens the preview once the person is signed in (`D262`).
 *
 * ── OPENING NEVER GENERATES, AND NEVER IMPORTS (`T-145`) ───────────────────
 * The preview READS the snapshot. Only a tap on "Revisar e importar" posts
 * the import, which writes a DRAFT, and the app then opens the review naming
 * that draft — and a review that names its draft never generates one.
 *
 * ── SIGNED IN, BOTH SIDES ──────────────────────────────────────────────────
 * Every route takes a bearer. The snapshot names nobody, but a public read
 * would make every shared plan crawlable; a person without an account meets
 * the intake first (`D262`), and the preview after it.
 */
export const PLAN_SHARE_ROUTES = {
    /** `POST` shares the plan in effect; `GET` lists the caller's live shares. */
    mine: "/me/plan/shares",
    /** `DELETE` revokes one of the caller's shares. */
    mineOne: "/me/plan/shares/:shareId",
    /** `GET` — the snapshot, for whoever holds the id. */
    shared: "/me/shared-plans/:shareId",
    /** `POST` — the snapshot becomes the caller's draft. */
    importShared: "/me/shared-plans/:shareId/import",
};
/** The query key a plan link carries its share id under. */
export const PLAN_SHARE_PARAM = "plan";
/** The server mints twelve characters of Crockford base 32, lowercase; the
 *  pattern accepts 8 to 32 so the length can change without a release. */
export const planShareId = z.string().regex(/^[0-9a-z]{8,32}$/);
export function myPlanSharePath(shareId) {
    return `${PLAN_SHARE_ROUTES.mine}/${encodeURIComponent(shareId)}`;
}
export function sharedPlanPath(shareId) {
    return `/me/shared-plans/${encodeURIComponent(shareId)}`;
}
export function importSharedPlanPath(shareId) {
    return `${sharedPlanPath(shareId)}/import`;
}
export const planShareSummary = z.object({
    createdAt: z.iso.datetime(),
    dayCount: z.int().nonnegative(),
    lineCount: z.int().nonnegative(),
    shareId: planShareId,
});
/** `GET /me/plan/shares` — newest first; revoked ones are gone. */
export const planSharesOutput = z.object({
    shares: z.array(planShareSummary),
});
export const sharedPlanLine = z.object({
    durationSeconds: z.int().positive().nullable(),
    exerciseId: z.string().min(1),
    /** In the reader's language (`?locale=`). */
    exerciseName: z.string(),
    repsMax: z.int().positive().nullable(),
    repsMin: z.int().positive().nullable(),
    restSeconds: z.int().nonnegative().nullable(),
    sets: z.int().positive().nullable(),
    /** `s1`, `s2`… — consecutive lines under one key are a superset. */
    supersetId: z.string().nullable(),
});
export const sharedPlanDay = z.object({
    lines: z.array(sharedPlanLine),
    name: z.string().nullable(),
});
/** `GET /me/shared-plans/:shareId` — the shape, no load, no note, nobody. */
export const sharedPlanOutput = z.object({
    createdAt: z.iso.datetime(),
    days: z.array(sharedPlanDay),
    /** Whether the caller is the one who shared it. */
    mine: z.boolean(),
    shareId: planShareId,
});
/** `POST /me/shared-plans/:shareId/import` — the draft the review opens. */
export const importSharedPlanOutput = z.object({
    draftPlanId: z.string().min(1),
});
/** Crockford's reading of what a person types: case, spaces and dashes
 *  ignored, and the letters it never emits read as the digits they look
 *  like (`i`/`l` → 1, `o` → 0). `null` when it cannot be a share id. */
export function parsePlanShareCode(typed) {
    const cleaned = typed
        .toLowerCase()
        .replace(/[\s-]/g, "")
        .replace(/[il]/g, "1")
        .replace(/o/g, "0");
    const parsed = planShareId.safeParse(cleaned);
    if (!parsed.success || cleaned.includes("u")) {
        return null;
    }
    return parsed.data;
}
/** `abcdefgh2345` → `ABCD EFGH 2345`: groups of four, as a person reads it. */
export function formatPlanShareCode(shareId) {
    return (shareId.toUpperCase().match(/.{1,4}/g) ?? []).join(" ");
}
/** A plan link: the share id, and the per-share ref of `D260` when there is
 *  one. Only drawn while the switch names a base (`D257`). */
export function planShareLinkFor(base, shareId, ref) {
    const url = new URL(ref === null ? base : shareLinkFor(base, ref));
    url.searchParams.set(PLAN_SHARE_PARAM, shareId);
    return url.toString();
}
//# sourceMappingURL=plan-share.js.map